/**
 * Suggestions inbox — pending agent proposals targeting tasks.
 *
 * The platform Suggestions API ships from F1.4; this screen surfaces the
 * pending queue so the user can accept or reject each one. Accept applies
 * the proposed patch server-side via `applyTaskPatch` and writes a
 * `decisions` row with `from_suggestion_id` set (see ADR 0028).
 *
 * Scope (T-next.1): list view, Accept/Reject buttons, status tabs.
 * Per-task row chip and topbar bell live in T-next.1b.
 */

import { useMemo, useState } from 'react';
import { SectionHead, Icon, Avatar } from '@k-os/ui';
import {
  useTaskSuggestions,
  useAcceptSuggestion,
  useRejectSuggestion,
  type SuggestionDto,
  type SuggestionStatus,
} from '../api/suggestions';
import { useAgents } from '../api/agents';
import styles from './Suggestions.module.css';

const TABS: ReadonlyArray<{ key: SuggestionStatus; label: string }> = [
  { key: 'pending', label: 'Pending' },
  { key: 'accepted', label: 'Recent accepted' },
  { key: 'rejected', label: 'Rejected' },
];

export function SuggestionsScreen() {
  const [tab, setTab] = useState<SuggestionStatus>('pending');
  const listQ = useTaskSuggestions(tab);
  const agentsQ = useAgents();

  const agentLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of agentsQ.data?.agents ?? []) map.set(a.id, a.label);
    return map;
  }, [agentsQ.data]);

  const suggestions = listQ.data?.suggestions ?? [];

  return (
    <div className={styles.shell}>
      <div className={styles.head}>
        <div>
          <h2 className={styles.title}>Suggestions</h2>
          <div className={styles.sub}>
            Agent proposals waiting on your review. Accepting applies the
            change; rejecting leaves the task untouched. Either way the
            decision is recorded.
          </div>
        </div>
      </div>

      <div className={styles.tabs} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <SectionHead
        title={tab === 'pending' ? 'Pending' : tab === 'accepted' ? 'Accepted' : 'Rejected'}
        count={suggestions.length}
      />

      {listQ.isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : suggestions.length === 0 ? (
        <div className={styles.empty}>
          {tab === 'pending'
            ? 'No pending suggestions. Agents propose changes here when they think a task needs one.'
            : tab === 'accepted'
              ? 'No accepted suggestions yet.'
              : 'No rejected suggestions yet.'}
        </div>
      ) : (
        <div className={styles.list}>
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              agentLabel={agentLabelById.get(s.agentId) ?? 'Agent'}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function SuggestionCard({
  suggestion,
  agentLabel,
}: {
  suggestion: SuggestionDto;
  agentLabel: string;
}) {
  const accept = useAcceptSuggestion();
  const reject = useRejectSuggestion();
  const pending = suggestion.status === 'pending';
  const busy = accept.isPending || reject.isPending;

  const proposedSummary = useMemo(() => describePatch(suggestion.proposed), [
    suggestion.proposed,
  ]);

  return (
    <article className={styles.card}>
      <header className={styles.cardHead}>
        <div className={styles.cardAgent}>
          <Avatar
            person={{ initials: initialsFor(agentLabel), color: '#5a7a4a' }}
            size={18}
          />
          <span className={styles.agentLabel}>{agentLabel}</span>
          <span className={styles.kind}>· {suggestion.kind}</span>
          {suggestion.confidence && (
            <span className={`${styles.confidence} ${styles[`conf_${suggestion.confidence}`]}`}>
              {suggestion.confidence}
            </span>
          )}
        </div>
        <span className={styles.created}>{formatRelative(suggestion.createdAt)}</span>
      </header>

      <p className={styles.rationale}>{suggestion.rationale}</p>

      {proposedSummary.length > 0 && (
        <ul className={styles.proposedList}>
          {proposedSummary.map((row, i) => (
            <li key={i} className={styles.proposedRow}>
              <span className={styles.proposedField}>{row.field}</span>
              <span className={styles.proposedArrow}>
                <Icon name="chevron" size={10} />
              </span>
              <span className={styles.proposedValue}>{row.value}</span>
            </li>
          ))}
        </ul>
      )}

      <footer className={styles.cardFoot}>
        <span className={styles.target}>
          Target: task <code className={styles.targetId}>{shortId(suggestion.targetId)}</code>
        </span>
        {pending && (
          <div className={styles.actions}>
            <button
              type="button"
              className="kos-btn"
              disabled={busy}
              onClick={() => reject.mutate(suggestion.id)}
            >
              Reject
            </button>
            <button
              type="button"
              className="kos-btn kos-btn-primary"
              disabled={busy}
              onClick={() => accept.mutate(suggestion.id)}
            >
              <Icon name="check" size={12} /> Accept
            </button>
          </div>
        )}
        {!pending && suggestion.resolvedAt && (
          <span className={styles.resolved}>
            {suggestion.status} · {formatRelative(suggestion.resolvedAt)}
          </span>
        )}
      </footer>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function describePatch(proposed: Record<string, unknown>): Array<{
  field: string;
  value: string;
}> {
  return Object.entries(proposed).map(([k, v]) => ({
    field: humaniseField(k),
    value: formatValue(v),
  }));
}

function humaniseField(key: string): string {
  return key
    .replace(/Id$/, '')
    .replace(/At$/, '')
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
}

function formatValue(v: unknown): string {
  if (v === null) return '—';
  if (v === undefined) return '—';
  if (typeof v === 'string') {
    // ISO date passthrough — friendly format
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleDateString();
    }
    return v;
  }
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
}

function initialsFor(label: string): string {
  return label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}
